// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { BaseItem, ColumnData } from './BaseItem';
import { PositionItem } from './PositionItem';
import { ColumnParams } from './ColumnParams';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { OrderTif, OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DynProperty } from '@shared/commons/DynProperty';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DataCache } from '@shared/commons/DataCache';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { WDSettings } from '../settings/WDGeneralSettings';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { OrderFormatter } from '@shared/commons/cache/Formatters/OrderFormatter';
import { StringUtils } from '@shared/utils/StringUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
export class OrderItem extends BaseItem {
    constructor(order, sessionSettings) {
        super(sessionSettings);
        this.userGroupName = '';
        this.Account_CorrectAsset = null;
        this.isOvernightTradingHours = () => {
            const order = this.FOrder;
            if (isNullOrUndefined(order)) {
                return false;
            }
            const isAllowOTHTrading = order.IsAllowOTHTrading;
            return !isNullOrUndefined(isAllowOTHTrading) && isAllowOTHTrading;
        };
        this.isTrailingSL = () => {
            const order = this.FOrder;
            if (isNullOrUndefined(order)) {
                return false;
            }
            return isValidNumber(order.StopLossPriceValue) && order.StopLossPriceType === SlTpPriceType.TrOffset;
        };
        this.FOrder = order;
        this.sessionSettings = sessionSettings;
        if (order) {
            this.itemId = OrderItem.GetItemId(order);
        }
        this.QuickTableEditingInfoMap[46] = (function () {
            return new QuickTableEditingInfo(DynProperty.CLOSE_BUTTON);
        })();
    }
    static GetItemId(order) {
        return order.OrderNumber; /* Женя: убрали. ай-ди используется также в BuilderOrdersPanel */ // + ((string.IsNullOrEmpty(order.DataCache.VendorName)) ? "" : "(" + order.DataCache.VendorName + ")");
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        switch (column) {
            case 0:
                return colors.tableSymbolForeground;
            case 1:
                if (!isNullOrUndefined(this.FOrder)) {
                    return this.FOrder.BuySell === OperationType.Buy ? colors.buyColor : colors.sellColor;
                }
                break;
            case 5:
                return colors.tableQuantityForeground;
        }
        return super.getQuickTableCellForeground(column);
    }
    GetTooltipKey(column) {
        switch (column) {
            case OrderItem.USER_COMMENT_COL_INDEX:
                return this.getColumnValue(column);
        }
        return '';
    }
    ColumnCount() {
        return OrderItem.columnsParams.length;
    }
    getColumnData(column) {
        var _a, _b, _c, _d, _e, _f, _g;
        let o = null;
        try {
            o = this.getColumnValue(column);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            o = 'some Error';
        }
        let FormattedValue = null;
        switch (column) {
            case 1:
                FormattedValue = this.FOrder.GetFullOperationName(false);
                break;
            case 2:
                // var time = o;
                // if (time.getFullYear() < 1000)
                //     return "";
                // FormattedValue = Utils.FormatToDate(o);
                // break;
                FormattedValue = DateTimeUtils.formatDate(o, 'DD.MM.YYYY  HH:mm:ss');
                break;
            case 3:
                FormattedValue = DateTimeUtils.FormatToTime(o);
                break;
            case OrderItem.ORDER_TYPE_COL_INDEX: {
                // Проверка,для этого ордера разрешен стоп-лосс?
                let parentOrder = null;
                try {
                    parentOrder = this.FOrder.DataCache.Trading.getPositionById(this.FOrder.BoundTo !== '-1' ? this.FOrder.BoundTo : this.FOrder.PositionId);
                    if (parentOrder == null) {
                        parentOrder = this.FOrder.DataCache.Trading.getOrderById(this.FOrder.BoundTo);
                    }
                }
                catch (ex) {
                    ErrorInformationStorage.GetException(ex);
                }
                if (this.FOrder.DataCache.isAllowedForAccount(RulesSet.FUNCTION_SLTP, this.FOrder.Account) ||
                    this.FOrder.IsGroupPart ||
                    parentOrder != null /* && parentOrder.IsGroupPart */) {
                    if (parentOrder != null) {
                        if (parentOrder.SLOrder != null &&
                            parentOrder.SLOrder.OrderNumber === this.FOrder.OrderNumber &&
                            parentOrder.SLOrder.OrderType === OrderType.TrailingStop) {
                            FormattedValue = 'Trailing Stop for ' + parentOrder.PositionId;
                        }
                        else if (parentOrder.SLOrder != null &&
                            parentOrder.SLOrder.OrderNumber === this.FOrder.OrderNumber) {
                            FormattedValue = 'S/L for ' + parentOrder.PositionId;
                        }
                        else if (parentOrder.TPOrder != null &&
                            parentOrder.TPOrder.OrderNumber === this.FOrder.OrderNumber) {
                            FormattedValue = 'T/P for ' + parentOrder.PositionId;
                        }
                    }
                }
                // Иначе просто возвращаем тип ордера
                break;
            }
            case 5:
                if (typeof o === 'number' && this.FOrder != null) {
                    FormattedValue = this.FOrder.DataCache.formatVolume(this.FOrder.Instrument, o, WDSettings.isQuantityInLots, this.FOrder.ProductType, this.FOrder.Account);
                }
                break;
            case 10:
            case 11:
                if (typeof o === 'number' && this.FOrder != null) {
                    FormattedValue = this.FOrder.DataCache.formatVolume(this.FOrder.Instrument, o, WDSettings.isQuantityInLots, this.FOrder.ProductType, this.FOrder.Account);
                }
                break;
            case 6:
                {
                    const price = this.FOrder != null
                        ? OrderUtils.GetCorrectPriceForOrderType(this.FOrder.Price, this.FOrder.StopLimit, OrderUtils.PriceType.LimitPrice, this.FOrder.OrderType)
                        : -1;
                    if (!isNaN(price)) {
                        FormattedValue = this.FOrder.Instrument.formatPrice(price);
                    }
                    else {
                        FormattedValue = '';
                    }
                    const trigger = this.FOrder.TriggerSLTP;
                    if (FormattedValue && trigger) {
                        FormattedValue += ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
                    }
                }
                break;
            case 7:
                {
                    const stopPrice = this.FOrder != null
                        ? OrderUtils.GetCorrectPriceForOrderType(this.FOrder.Price, this.FOrder.StopLimit, OrderUtils.PriceType.StopPrice, this.FOrder.OrderType)
                        : -1;
                    if (!isNaN(stopPrice)) {
                        FormattedValue = this.FOrder.Instrument.formatPrice(stopPrice);
                    }
                    else {
                        FormattedValue = '';
                    }
                    const trigger = this.FOrder.TriggerSLTP;
                    if (FormattedValue && trigger) {
                        FormattedValue += ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(trigger);
                    }
                }
                break;
            case 8:
                if (this.FOrder != null && typeof o === 'number' && !isNaN(o)) {
                    FormattedValue = this.FOrder.Instrument.formatPrice(o);
                }
                else {
                    FormattedValue = Resources.getResource('general.trading.priceNotAvailable');
                }
                break;
            case 21:
                FormattedValue = '';
                break;
            case 22:
                FormattedValue = '';
                break;
            case 23: {
                // под ван позишн, http://tp.pfsoft.net/Project/QA/Bug/View.aspx?BugID=16343&acid=74FA48A38CBEE17F6445F76C9BEC721B
                const parentOrder2 = null;
                // TODO
                // if (this.FOrder != null && this.FOrder.DataCache != null)
                // {
                //    parentOrder2 = this.FOrder.DataCache.Trading.getPositionById(this.FOrder.BoundTo);
                //    if (parentOrder2 == null)
                //        parentOrder2 = this.FOrder.DataCache.Trading.getOrderById(this.FOrder.BoundTo);
                // }
                FormattedValue = parentOrder2 != null ? parentOrder2.OrderNumber : o;
                break;
            }
            case 31:
                FormattedValue = OrderFormatter.OrderStatusStr(this.FOrder);
                break;
            case 32:
                if (((_a = this.FOrder) === null || _a === void 0 ? void 0 : _a.Instrument) != null && this.FOrder.Account != null) {
                    FormattedValue =
                        this.Account_CorrectAsset != null
                            ? this.Account_CorrectAsset.formatPrice(this.FOrder.getInitMargin(true))
                            : this.FOrder.Account.formatPrice(this.FOrder.getInitMargin(true));
                }
                else {
                    FormattedValue = '';
                }
                break;
            case 33: // StrikePrice
                if (o > 0) {
                    if (((_b = this.FOrder) === null || _b === void 0 ? void 0 : _b.Instrument) != null) {
                        FormattedValue = this.FOrder.Instrument.formatPrice(o);
                    }
                    else {
                        FormattedValue = o.toString();
                    }
                }
                else {
                    FormattedValue = '';
                }
                break;
            case 34:
                if (o && o.getTime() > 0) {
                    FormattedValue = DateTimeUtils.FormatToDate(o);
                }
                else {
                    FormattedValue = '';
                }
                break;
            case 35:
                if (this.FOrder != null &&
                    !((this.FOrder.OrderType === OrderType.Market || this.FOrder.OrderType === OrderType.Limit) &&
                        this.FOrder.TimeInForce === OrderTif.Day &&
                        this.FOrder.Instrument.InstrType !== InstrumentTypes.OPTIONS &&
                        this.FOrder.Instrument.InstrType !== InstrumentTypes.FUTURES &&
                        !this.FOrder.Instrument.CFD)) {
                    FormattedValue = '';
                }
                else if (typeof o === 'number' && this.FOrder != null) {
                    FormattedValue = this.FOrder.DataCache.formatVolume(this.FOrder.Instrument, o, WDSettings.isQuantityInLots, this.FOrder.ProductType, this.FOrder.Account);
                }
                if (o === 0) {
                    FormattedValue = '';
                }
                break;
            case 37:
                if (typeof o === 'number') {
                    FormattedValue = Resources.getResource('panel.orders.ExternalOrderStatus_' + o);
                }
                else {
                    FormattedValue = '';
                }
                break;
            case OrderItem.AVERAGE_FILLED_PRICE_COL_INDEX:
                if (o === 0) {
                    FormattedValue = '';
                }
                else {
                    FormattedValue = OrderFormatter.AverageFilledPriceStr(this.FOrder);
                }
                break;
            case OrderItem.SL_PRICE_COL_INDEX:
            case OrderItem.SLL_PRICE_COL_INDEX:
            case OrderItem.TP_PRICE_COL_INDEX: {
                const isTP = column === OrderItem.TP_PRICE_COL_INDEX;
                if (!isValidNumber(o)) {
                    FormattedValue = '';
                }
                else if (!isNullOrUndefined((_c = this.FOrder) === null || _c === void 0 ? void 0 : _c.Instrument)) {
                    FormattedValue = this.FOrder.Instrument.formatPrice(o);
                }
                else {
                    FormattedValue = o.toString();
                }
                const sltpTrigger = (isTP ? this.FOrder.TriggerTP : this.FOrder.TriggerSL) || this.FOrder.TriggerSLTP;
                if (FormattedValue && sltpTrigger) {
                    FormattedValue += ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(sltpTrigger);
                }
                break;
            }
            case OrderItem.SL_OFFSET_COL_INDEX:
            case OrderItem.SLL_OFFSET_COL_INDEX:
            case OrderItem.TP_OFFSET_COL_INDEX: {
                const isTP = column === OrderItem.TP_OFFSET_COL_INDEX;
                if (!isValidNumber(o)) {
                    FormattedValue = '';
                }
                else if (!isNullOrUndefined((_d = this.FOrder) === null || _d === void 0 ? void 0 : _d.Instrument)) {
                    const visualOffsetType = isTP
                        ? SlTpUtils.getTPVisualOffsetType(this.FOrder.Instrument, this.FOrder.createSlTpHolder())
                        : ((_e = SlTpUtils.getSLVisualOffsetType(this.FOrder.Instrument, this.FOrder.createSlTpHolder())) !== null && _e !== void 0 ? _e : SlTpUtils.getDefaultVisualOffsetType(this.FOrder.Instrument));
                    const visualOffset = SlTpUtils.toVisualValue(o, this.FOrder.Instrument, visualOffsetType);
                    const formattedOffset = SlTpUtils.formatSLTPValue(visualOffset, this.FOrder.Instrument, visualOffsetType);
                    FormattedValue = `${formattedOffset} ${SlTpUtils.getTextForOffsetMode(visualOffsetType)}`;
                }
                else {
                    FormattedValue = `${o.toString()} ${SlTpUtils.getTextForOffsetMode(OffsetModeViewEnum.Ticks)}`;
                }
                const sltpTrigger = (isTP ? this.FOrder.TriggerTP : this.FOrder.TriggerSL) || this.FOrder.TriggerSLTP;
                if (FormattedValue && sltpTrigger) {
                    FormattedValue += ' ' + SLTPTrigger.ConvertBidAskRawValueToStr(sltpTrigger);
                }
                break;
            }
            case 45:
                if (((_f = this.FOrder) === null || _f === void 0 ? void 0 : _f.Instrument) != null) {
                    FormattedValue = InstrumentUtils.GetLocalizedProductType(this.FOrder.Instrument, o);
                }
                break;
            case 49:
                FormattedValue = OrderUtils.GetFormattedLeverage(o, true) || StringUtils.NOT_AVAILABLE;
                break;
            // default:
            // if (this.FOrder != null && this.FOrder.Instrument != null)
            // {
            //    if (o is String)
            //    return o.ToString();
            //    if (o is int)
            //    return this.FOrder.Instrument.formatPrice( o);
            //    if (o is double)
            //    return this.FOrder.Instrument.formatPrice( o);
            //    if (o is DateTime)
            //    return ( o).ToString();
            // }
            // return o != null ? o.ToString() : "";
        }
        if (FormattedValue === null) {
            if (((_g = this.FPosition) === null || _g === void 0 ? void 0 : _g.Instrument) != null) {
                if (typeof o === 'number') {
                    FormattedValue = this.FPosition.Instrument.formatPrice(o);
                }
                else {
                    FormattedValue = o.toString();
                }
            }
            else {
                FormattedValue = o != null ? o.toString() : '';
            }
        }
        return new ColumnData(o, FormattedValue);
    }
    getColumnValue(column) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const orderParameterContainer = DataCache.OrderParameterContainer;
        switch (column) {
            case 0:
                return this.FOrder.Instrument.DisplayName();
            case 1:
                return this.FOrder.BuySell;
            case 2:
                return this.FOrder != null
                    ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(this.FOrder.UTCDateTime)
                    : new Date();
            case 3:
                return this.FOrder != null
                    ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(this.FOrder.UTCDateTime)
                    : new Date();
            case OrderItem.ORDER_TYPE_COL_INDEX: {
                // Потом надо завести тип ордере - Stop пока такая заплатка
                if (isNullOrUndefined(this.FOrder)) {
                    return '';
                }
                const orderTypeObj = orderParameterContainer.GetOrderType(this.FOrder.OrderType);
                return this.FOrder.OrderType === OrderType.Market &&
                    this.FOrder.BoundTo !== '-1' &&
                    this.FOrder.BoundTo !== ''
                    ? Resources.getResource('general.trading.marketCloseFor') + ' ' + this.FOrder.BoundTo
                    : Resources.getResource('property.' + orderTypeObj.localizationKey());
            }
            case 5:
                if (this.FOrder != null) {
                    // TODO. Shitty code.
                    return PositionItem.GetPositionAmountText(this.FOrder);
                }
                else {
                    return 0;
                }
            case 6:
                return this.FOrder != null
                    ? OrderUtils.GetCorrectPriceForOrderType(this.FOrder.Price, this.FOrder.StopLimit, OrderUtils.PriceType.LimitPrice, this.FOrder.OrderType)
                    : NaN;
            case 7:
                return this.FOrder != null
                    ? OrderUtils.GetCorrectPriceForOrderType(this.FOrder.Price, this.FOrder.StopLimit, OrderUtils.PriceType.StopPrice, this.FOrder.OrderType)
                    : NaN;
            case 8:
                return this.FOrder != null ? this.FOrder.CurPriceOpen : NaN;
            case 9:
                // TODO
                if (this.FOrder != null) {
                    let tif = Resources.getResource('property.' + OrderTifMap[this.FOrder.TimeInForce]);
                    if (this.FOrder.TimeInForce === OrderTif.GTD) {
                        const dtString = DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SHOW_TIME_FOR_GTD)
                            ? DateTimeUtils.formatDate(this.FOrder.ExpireAt, 'DD.MM.YYYY')
                            : this.FOrder.ExpireAt.toLocaleDateString();
                        tif += ' (' + dtString + ')';
                    }
                    return tif;
                }
                else {
                    return '';
                }
            case 10:
                if (this.FOrder != null) {
                    if (!WDSettings.isQuantityInLots) {
                        return this.FOrder.SharesFilled * this.FOrder.Instrument.LotSize;
                    }
                    else {
                        return this.FOrder.SharesFilled;
                    }
                }
                else {
                    return 0.0;
                }
            case 11:
                if (this.FOrder != null) {
                    if (!WDSettings.isQuantityInLots) {
                        return this.FOrder.AmountRemaining * this.FOrder.Instrument.LotSize;
                    }
                    else {
                        return this.FOrder.AmountRemaining;
                    }
                }
                else {
                    return 0.0;
                }
            case 12:
                return '';
            case 13:
                return this.FOrder != null ? this.FOrder.Route : '';
            case 14:
                return '';
            case 15:
                return this.FOrder != null ? this.FOrder.OrderNumber /* FOrder.OrderNumber */ : '';
            case 16:
                return this.FOrder != null ? this.FOrder.Account : null;
            case 17:
                return this.FOrder != null ? this.FOrder.OrderCreatedByUser : '';
            case 18:
                return this.FOrder != null ? this.FOrder.RemoteID : '';
            case 19:
                return '';
            case 20:
                if (((_b = (_a = this.FOrder) === null || _a === void 0 ? void 0 : _a.Instrument) === null || _b === void 0 ? void 0 : _b.LastQuote) != null) {
                    return this.FOrder.Instrument.LastQuote.Route;
                }
                else {
                    return '';
                }
            case 21:
                return NaN;
            case 22:
                return NaN;
            case 23:
                if (this.FOrder != null) {
                    return this.FOrder.BoundTo !== '-1'
                        ? this.FOrder.BoundTo
                        : this.FOrder.PositionId !== '-1'
                            ? this.FOrder.PositionId
                            : '';
                }
                else {
                    return '';
                }
            case OrderItem.COMMENT_COL_INDEX:
                return (_d = (_c = this.FOrder) === null || _c === void 0 ? void 0 : _c.GetComment()) !== null && _d !== void 0 ? _d : '';
            case 25:
                if (((_e = this.FOrder) === null || _e === void 0 ? void 0 : _e.Instrument) != null) {
                    return InstrumentUtils.getInstrumentTypeStringLocalized(this.FOrder.Instrument.InstrType, this.FOrder.Instrument.CFD);
                }
                return '';
            // +++yura
            case 26:
                if (this.FOrder != null) {
                    const account = this.FOrder.Account;
                    if (account != null) {
                        return account.userLogin;
                    }
                    else {
                        return '';
                    }
                }
                else {
                    return '';
                }
            case 27:
                return ((_f = this.FOrder) === null || _f === void 0 ? void 0 : _f.Instrument) != null ? this.FOrder.Instrument.DescriptionValue() : '';
            case 28:
                if (this.FOrder.Instrument != null) {
                    return this.FOrder.Instrument.TradingExchange;
                }
                return '';
            case 29:
                if (this.FOrder !== null) {
                    if (this.FOrder.IsGroupPart) {
                        return this.FOrder.OrderGroupId.toString();
                    }
                    else {
                        return '';
                    }
                }
                else {
                    return '';
                }
            case 30:
                if (this.userGroupName !== null) {
                    return this.userGroupName;
                }
                else {
                    return '';
                }
            case 31:
                return this.FOrder.Status;
            case 32:
                return this.FOrder !== null
                    ? this.FOrder.getInitMargin(PositionItem.UseAccCurrency()) // FOrder.getInitMargin(true)
                    : 0;
            case 33:
                if (this.FOrder !== null && this.FOrder.StrikePrice > 0) {
                    return this.FOrder.StrikePrice;
                }
                else {
                    return 0;
                }
            case 34:
                if (((_h = (_g = this.FOrder) === null || _g === void 0 ? void 0 : _g.Instrument) === null || _h === void 0 ? void 0 : _h.ExpDateReal.getFullYear()) > 2000) {
                    return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(this.FOrder.Instrument.ExpDateReal);
                }
                else {
                    return DateTimeUtils._ZeroTime;
                }
            case 35:
                if (this.FOrder != null && this.FOrder.TimeInForce === OrderTif.Day) {
                    let sign = 1;
                    // if (this.FOrder.BuySell != OperationType.Buy && this.sessionSettings.ShowAmountSign)
                    //     sign = -1;
                    if (this.FOrder.DisclosedQty < 0 && sign < 0) {
                        sign = 1;
                    }
                    if (!WDSettings.isQuantityInLots) {
                        return this.FOrder.DisclosedQty * this.FOrder.Instrument.LotSize * sign;
                    }
                    else {
                        return this.FOrder.DisclosedQty * sign;
                    }
                }
                else {
                    return 0;
                }
            case 36:
                return this.FOrder !== null ? this.FOrder.ExternalOrderId : '';
            case 37:
                return this.FOrder !== null ? this.FOrder.ExternalStatus : '';
            case OrderItem.AVERAGE_FILLED_PRICE_COL_INDEX:
                return OrderFormatter.AverageFilledPrice(this.FOrder);
            case OrderItem.USER_COMMENT_COL_INDEX:
                return (_k = (_j = this.FOrder) === null || _j === void 0 ? void 0 : _j.GetComment()) !== null && _k !== void 0 ? _k : '';
            case OrderItem.SL_PRICE_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetStopLossInPriceValue() : NaN;
            case 42:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetTakeProfitInPriceValue() : NaN;
            case OrderItem.SL_OFFSET_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetStopLossInOffsetValue() : NaN;
            case OrderItem.TP_OFFSET_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetTakeProfitInOffsetValue() : NaN;
            case 45: {
                let productType = ProductType.General;
                if ((_l = this.FOrder) === null || _l === void 0 ? void 0 : _l.ProductType) {
                    productType = this.FOrder.ProductType;
                }
                return productType;
            }
            case OrderItem.SLL_PRICE_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetStopLossLimitInPriceValue() : NaN;
            case OrderItem.SLL_OFFSET_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.GetStopLossLimitInOffsetValue() : NaN;
            case 49: // Leverage
                return ((_m = this.FOrder) === null || _m === void 0 ? void 0 : _m.Leverage) ? this.FOrder.Leverage : null;
            case OrderItem.STRATEGY_ID_COL_INDEX:
                return !isNullOrUndefined(this.FOrder) ? this.FOrder.StrategyId : NaN;
            default:
                return '';
        }
    }
    GetColumnParams(column) {
        const result = OrderItem.columnsParams[column];
        const isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        result.Hidden = isHidden;
        if (column === 3 ||
            column === 13 ||
            column === 32 ||
            column === 30 ||
            column === 21 ||
            column === 22 /* какое-то правило №3 */ ||
            column === 12 ||
            column === 14 ||
            (column >= 17 && column <= 20)) {
            result.Hidden = true;
        }
        // Product type
        if (column === 45) {
            result.Hidden = !RiskPlan.showProductType;
        }
        if ((column === OrderItem.SL_OFFSET_COL_INDEX ||
            column === OrderItem.TP_OFFSET_COL_INDEX ||
            column === OrderItem.SLL_PRICE_COL_INDEX ||
            column === OrderItem.SLL_OFFSET_COL_INDEX) &&
            IsAllowed.IsProtectiveOrders()) {
            result.Hidden = true;
        } // #108200 doc 4.2
        if (column === OrderItem.USER_COMMENT_COL_INDEX) {
            result.Hidden = !IsAllowed.IsUserCommentAllowed();
        }
        return result;
    }
    fireUpdate() { }
    GetCurrentAccount() {
        return this.FOrder.Account;
    }
    GetCurrentInstrument() {
        return this.FOrder.Instrument;
    }
    GetOrderType() {
        return this.FOrder.OrderType;
    }
    InstrumentName() {
        return this.FOrder.Instrument.GetInteriorID();
    }
    DisplayInstrumentName() {
        return this.FOrder.Instrument.DisplayName();
    }
    isAllowEdit(columnIndex) {
        switch (columnIndex) {
            case OrderItem.QUANTITY_COL_INDEX:
            case OrderItem.PRICE_COL_INDEX:
            case OrderItem.STOP_PRICE_COL_INDEX:
            case OrderItem.SL_PRICE_COL_INDEX:
            case OrderItem.TP_PRICE_COL_INDEX:
            case OrderItem.SL_OFFSET_COL_INDEX:
            case OrderItem.TP_OFFSET_COL_INDEX:
            case OrderItem.SLL_PRICE_COL_INDEX:
            case OrderItem.SLL_OFFSET_COL_INDEX:
                return (!TradingLockUtils.TradingLock.tradingLocked &&
                    IsAllowed.IsOrderModifyingAllowed(this.FOrder).Allowed);
            default:
                return super.isAllowEdit(columnIndex);
        }
    }
    NeedSortMenu() {
        return true;
    }
    GetMenuIndex(headerKey) {
        if (OrderItem.ListMenuIndex.Contains(headerKey)) {
            return OrderItem.ListMenuIndex.indexOf(headerKey);
        }
        else {
            return 1000;
        }
    }
}
/// <summary>
/// Здесь хранятся настройки колонок (один экземпляр на класс)
/// </summary>
OrderItem.columnsParams = [
    //   /headerKey /width /columnType /showTotal /visible /allowGrouping /hidden /beginGroupFlag /emptyValue
    /* 0 */ new ColumnParams('panel.orders.symbol', 65, QTAlignment.Left, TableTotalsVisibility.All, true, true),
    new ColumnParams('panel.orders.action', 65, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.orders.date_time', 65, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.orders.time', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.type', 65, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    /* 5 */ new ColumnParams('panel.orders.quantity', 65, QTAlignment.Right, TableTotalsVisibility.All, true, false, null, null, null),
    new ColumnParams('panel.orders.price', 65, QTAlignment.Right, TableTotalsVisibility.None, true, false, null, null, null),
    new ColumnParams('panel.orders.StopPrice', 65, QTAlignment.Right, TableTotalsVisibility.None, true, false, null, null, null),
    new ColumnParams('panel.orders.CurrentPrice', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.TIF', 65, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    /* 10 */ new ColumnParams('panel.orders.QuantityFilled', 65, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.orders.QuantityRemaining', 65, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.orders.Event', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Route', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Token', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 15 */ new ColumnParams('panel.orders.Reference', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Account', 65, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.orders.TraderID', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.RemoteID', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Ct_Ahead', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 20 */ new ColumnParams('panel.orders.Market', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.SL', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.orders.TP', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.orders.BindTo', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Comments', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 25 */ new ColumnParams('panel.orders.Instr_Type', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.orders.Login', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.symbolDescription', 65, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.orders.ExchangeTrading', 65, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.orders.GroupId', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 30 */ new ColumnParams('panel.orders.UserGroup', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Status', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.InitReq', 70, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.orders.StrikePrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.ExpirationDate', 40, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 35 */ new ColumnParams('panel.orders.disclosedQty', 65, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.orders.ExternalOrderId', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.ExternalOrderStatus', 65, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.orders.OCOGrouID', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.AvgFilledPrice', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 40 */ new ColumnParams('panel.orders.Comment', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.SL_Price', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.orders.TP_Price', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.orders.SL_Offset', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.orders.TP_Offset', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    /* 45 */ new ColumnParams('panel.orders.ProductType', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.orders.Cancel', 70, QTAlignment.Center, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.orders.SLL_Price', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.orders.SLL_Offset', 65, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.orders.Leverage', 65, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    /* 50 */ new ColumnParams('panel.orders.StrategyID', 65, QTAlignment.Left, TableTotalsVisibility.None, false, false)
];
OrderItem.ORDER_TYPE_COL_INDEX = 4;
OrderItem.QUANTITY_COL_INDEX = 5;
OrderItem.PRICE_COL_INDEX = 6;
OrderItem.STOP_PRICE_COL_INDEX = 7;
OrderItem.COMMENT_COL_INDEX = 24;
OrderItem.AVERAGE_FILLED_PRICE_COL_INDEX = 39;
OrderItem.USER_COMMENT_COL_INDEX = 40;
OrderItem.SL_PRICE_COL_INDEX = 41;
OrderItem.TP_PRICE_COL_INDEX = 42;
OrderItem.SL_OFFSET_COL_INDEX = 43;
OrderItem.TP_OFFSET_COL_INDEX = 44;
OrderItem.SLL_PRICE_COL_INDEX = 47;
OrderItem.SLL_OFFSET_COL_INDEX = 48;
OrderItem.STRATEGY_ID_COL_INDEX = 50;
OrderItem.ListMenuIndex = [
    'panel.orders.Account',
    'panel.orders.Login',
    'panel.orders.Route',
    'panel.orders.symbol',
    'panel.orders.symbolDescription',
    'panel.orders.Instr_Type',
    'panel.orders.Reference',
    'panel.orders.GroupId',
    'panel.orders.action',
    'panel.orders.type',
    'panel.orders.price',
    'panel.orders.CurrentPrice',
    'panel.orders.quantity',
    'panel.orders.disclosedQty',
    'panel.orders.date_time',
    'panel.orders.date',
    'panel.orders.time',
    'panel.orders.QuantityRemaining',
    'panel.orders.QuantityFilled',
    'panel.orders.AvgFilledPrice',
    'panel.orders.TIF',
    'panel.orders.StopPrice',
    'panel.orders.SL',
    'panel.orders.TP',
    'panel.orders.InitReq',
    'panel.orders.Status',
    'panel.orders.BindTo',
    'panel.orders.StrikePrice',
    'panel.orders.ExpirationDate',
    'panel.orders.ExternalOrderId',
    'panel.orders.ExternalOrderStatus',
    'panel.orders.SL_Price',
    'panel.orders.TP_Price',
    'panel.orders.SL_Offset',
    'panel.orders.TP_Offset',
    'panel.orders.SLL_Price',
    'panel.orders.SLL_Offset',
    'panel.orders.Leverage',
    'panel.orders.OCOGrouID',
    'panel.orders.ExchangeTrading',
    'panel.orders.StrategyID',
    'panel.orders.Cancel'
];
